/**
 * End-user Widget for Warpway SDK
 *
 * Provides a chat-based interface for end users to trigger automated actions
 */

import { Executor } from './executor'
import { getApiBaseUrl } from './api'
import { getIconSVG, type IconType } from './icons'

export interface WidgetConfig {
  siteId: string
  enabled: boolean
  brandColor?: string
  title?: string
  iconType?: IconType
  logoUrl?: string
  position?: 'bottom-right' | 'bottom-left'
  offsetX?: number
  offsetY?: number
  zIndex?: number
  hotkeyEnabled?: boolean
  hotkeyCombo?: string
  executionSpeed?: number
  showAnimations?: boolean
  autoOpen?: boolean
  plan?: string
}

interface ConversationMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
}

export class Widget {
  private config: WidgetConfig
  private container: HTMLElement | null = null
  private fab: HTMLElement | null = null
  private chatWindow: HTMLElement | null = null
  private isOpen: boolean = false
  private isExecuting: boolean = false
  private executor: Executor
  private executionSpeed: number = 0.7 // Default human-like speed
  private showAnimations: boolean = true // Default enabled
  private conversationHistory: ConversationMessage[] = []
  private sessionId: string = ''
  private conversationState: string = 'greeting'
  private conversationId: string = ''
  private hasShownWelcome: boolean = false

  constructor(config: WidgetConfig) {
    this.config = {
      brandColor: '#4f46e5',
      title: 'How can I help?',
      position: 'bottom-right',
      offsetX: 20,
      offsetY: 20,
      executionSpeed: 0.7,
      showAnimations: true,
      ...config,
    }

    // Set execution settings from config
    this.executionSpeed = this.config.executionSpeed ?? 0.7
    this.showAnimations = this.config.showAnimations ?? true

    this.executor = new Executor({
      speedMultiplier: this.executionSpeed,
      showAnimations: this.showAnimations,
      siteId: this.config.siteId,
      apiUrl: getApiBaseUrl(),
    })

    // Initialize or retrieve session ID
    this.sessionId = this.getOrCreateSessionId()
  }

  /**
   * Get or create a session ID for tracking conversations
   */
  private getOrCreateSessionId(): string {
    const storageKey = 'warpway_session_id'
    let sessionId = localStorage.getItem(storageKey)

    if (!sessionId) {
      sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`
      localStorage.setItem(storageKey, sessionId)
    }

    return sessionId
  }

  /**
   * Get the saved widget open state from localStorage
   */
  private getWidgetOpenState(): boolean {
    const storageKey = 'warpway_widget_open'
    const saved = localStorage.getItem(storageKey)
    return saved === 'true'
  }

  /**
   * Save the widget open state to localStorage
   */
  private saveWidgetOpenState(isOpen: boolean): void {
    const storageKey = 'warpway_widget_open'
    localStorage.setItem(storageKey, isOpen ? 'true' : 'false')
  }

  /**
   * Get saved conversation history from localStorage
   */
  private getConversationHistory(): ConversationMessage[] {
    const storageKey = `warpway_conversation_${this.config.siteId}`
    const saved = localStorage.getItem(storageKey)
    if (!saved) return []

    try {
      return JSON.parse(saved)
    } catch (error) {
      console.error('[Warpway Widget] Failed to parse conversation history:', error)
      return []
    }
  }

  /**
   * Save conversation history to localStorage
   */
  private saveConversationHistory(): void {
    const storageKey = `warpway_conversation_${this.config.siteId}`
    localStorage.setItem(storageKey, JSON.stringify(this.conversationHistory))
  }

  /**
   * Clear conversation history
   */
  private clearConversationHistory(): void {
    this.conversationHistory = []
    const storageKey = `warpway_conversation_${this.config.siteId}`
    localStorage.removeItem(storageKey)

    // Clear messages from DOM
    const messagesContainer = this.chatWindow?.querySelector('#warpway-widget-messages')
    if (messagesContainer) {
      messagesContainer.innerHTML = ''
    }

    // Reset state
    this.conversationState = 'greeting'

    // Show welcome message again
    this.addMessage("Hi! I can help you take actions on this site. What would you like to do?", 'bot')

    // Mark as shown to prevent double welcome message on reopen
    this.hasShownWelcome = true
  }

  /**
   * Mount the widget to the page
   */
  mount(): void {
    if (!this.config.enabled) {
      console.log('[Warpway Widget] Widget disabled, skipping mount')
      return
    }

    if (typeof document === 'undefined') {
      return
    }

    this.injectStyles()
    this.createFAB()
    this.createChatWindow()

    if (document.body) {
      document.body.appendChild(this.fab!)
      document.body.appendChild(this.chatWindow!)

      // Restore conversation history
      this.restoreConversationHistory()

      // Restore previous state or auto-open if configured
      const wasOpen = this.getWidgetOpenState()
      if (wasOpen || this.config.autoOpen) {
        this.open()
      }
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        document.body.appendChild(this.fab!)
        document.body.appendChild(this.chatWindow!)

        // Restore conversation history
        this.restoreConversationHistory()

        // Restore previous state or auto-open if configured
        const wasOpen = this.getWidgetOpenState()
        if (wasOpen || this.config.autoOpen) {
          this.open()
        }
      })
    }
  }

  /**
   * Inject CSS styles for the widget
   */
  private injectStyles(): void {
    const styleId = 'warpway-widget-styles'
    if (document.getElementById(styleId)) return

    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      .warpway-widget-fab {
        position: fixed;
        ${this.config.position?.includes('right') ? 'right' : 'left'}: ${this.config.offsetX}px;
        bottom: ${this.config.offsetY}px;
        width: 56px;
        height: 56px;
        border-radius: 28px;
        background: ${this.config.brandColor};
        color: white;
        border: none;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2147483646;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
      }

      .warpway-widget-fab:hover {
        transform: scale(1.1);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
      }

      .warpway-widget-fab-icon {
        width: 24px;
        height: 24px;
        transition: transform 0.3s ease;
      }

      .warpway-widget-fab-logo {
        width: 32px;
        height: 32px;
        object-fit: contain;
        border-radius: 4px;
      }

      .warpway-widget-fab-open .warpway-widget-fab-icon {
        transform: rotate(45deg);
      }

      .warpway-widget-chat {
        position: fixed;
        ${this.config.position?.includes('right') ? 'right' : 'left'}: ${this.config.offsetX}px;
        bottom: ${(this.config.offsetY || 20) + 72}px;
        width: 380px;
        height: 500px;
        background: white;
        border-radius: 16px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
        z-index: 2147483645;
        display: none;
        flex-direction: column;
        overflow: hidden;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
      }

      .warpway-widget-chat-open {
        display: flex;
      }

      .warpway-widget-header {
        background: ${this.config.brandColor};
        color: white;
        padding: 16px 20px;
        font-weight: 600;
        font-size: 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .warpway-widget-header-content {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .warpway-widget-header-logo {
        width: 28px;
        height: 28px;
        object-fit: contain;
        border-radius: 4px;
        background: rgba(255, 255, 255, 0.1);
        padding: 2px;
      }

      .warpway-widget-header-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .warpway-widget-new-chat,
      .warpway-widget-close {
        background: transparent;
        border: none;
        color: white;
        cursor: pointer;
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0.9;
        transition: opacity 0.2s, transform 0.2s;
      }

      .warpway-widget-new-chat:hover,
      .warpway-widget-close:hover {
        opacity: 1;
        transform: scale(1.05);
      }

      .warpway-widget-new-chat:active,
      .warpway-widget-close:active {
        transform: scale(0.95);
      }

      .warpway-widget-messages {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
        padding-bottom: 0;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .warpway-widget-message {
        max-width: 80%;
        padding: 10px 14px;
        border-radius: 12px;
        font-size: 14px;
        line-height: 1.5;
        position: relative;
      }

      .warpway-widget-message::before {
        content: attr(data-sender);
        font-size: 11px;
        font-weight: 600;
        opacity: 0.7;
        display: block;
        margin-bottom: 4px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .warpway-widget-message-bot {
        background: #f3f4f6;
        color: #1f2937;
        align-self: flex-start;
        border-bottom-left-radius: 4px;
      }

      .warpway-widget-message-bot::before {
        content: 'Assistant';
        color: #6b7280;
      }

      .warpway-widget-message-user {
        background: ${this.config.brandColor};
        color: white;
        align-self: flex-end;
        border-bottom-right-radius: 4px;
      }

      .warpway-widget-message-user::before {
        content: 'You';
        color: rgba(255, 255, 255, 0.8);
      }

      .warpway-widget-message-system {
        background: #fef3c7;
        color: #92400e;
        align-self: center;
        font-size: 13px;
        max-width: 90%;
        text-align: center;
      }

      .warpway-widget-input-container {
        padding: 16px;
        border-top: 1px solid #e5e7eb;
        display: flex;
        gap: 8px;
      }

      .warpway-widget-input {
        flex: 1;
        padding: 10px 14px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font-size: 14px;
        color: #111827;
        background: white;
        outline: none;
        font-family: inherit;
        resize: none;
        min-height: 40px;
        max-height: 120px;
        overflow-y: auto;
      }

      .warpway-widget-input:focus {
        border-color: ${this.config.brandColor};
      }

      .warpway-widget-send {
        padding: 10px 16px;
        background: ${this.config.brandColor};
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: opacity 0.2s ease;
      }

      .warpway-widget-send:hover:not(:disabled) {
        opacity: 0.9;
      }

      .warpway-widget-send:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .warpway-widget-loading {
        display: inline-block;
        width: 12px;
        height: 12px;
        border: 2px solid rgba(0, 0, 0, 0.1);
        border-radius: 50%;
        border-top-color: ${this.config.brandColor};
        animation: warpway-spin 0.8s linear infinite;
      }

      @keyframes warpway-spin {
        to { transform: rotate(360deg); }
      }

      .warpway-widget-typing-indicator {
        max-width: 80%;
        padding: 10px 14px;
        border-radius: 12px;
        background: #f3f4f6;
        color: #1f2937;
        align-self: flex-start;
        border-bottom-left-radius: 4px;
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .warpway-widget-typing-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #9ca3af;
        animation: warpway-typing 1.4s infinite;
      }

      .warpway-widget-typing-dot:nth-child(2) {
        animation-delay: 0.2s;
      }

      .warpway-widget-typing-dot:nth-child(3) {
        animation-delay: 0.4s;
      }

      @keyframes warpway-typing {
        0%, 60%, 100% {
          opacity: 0.3;
          transform: translateY(0);
        }
        30% {
          opacity: 1;
          transform: translateY(-4px);
        }
      }

      .warpway-widget-footer {
        padding: 8px 16px;
        text-align: center;
        font-size: 12px;
        color: #6b7280;
        border-top: 1px solid #e5e7eb;
        background: #fafafa;
      }

      .warpway-widget-footer a {
        color: #6b7280;
        text-decoration: none;
        font-weight: 500;
        transition: color 0.2s;
      }

      .warpway-widget-footer a:hover {
        color: #374151;
        text-decoration: underline;
      }

      .warpway-widget-action-pills-container {
        border-top: 1px solid #f3f4f6;
        flex-shrink: 0;
        background: #fafafa;
      }

      .warpway-widget-action-pills-header {
        padding: 6px 16px 2px;
        font-size: 10px;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .warpway-widget-action-pills {
        padding: 0 16px 8px;
        overflow-x: auto;
        display: flex;
        gap: 6px;
        scrollbar-width: thin;
        scrollbar-color: #d1d5db transparent;
      }

      .warpway-widget-action-pills::-webkit-scrollbar {
        height: 4px;
      }

      .warpway-widget-action-pills::-webkit-scrollbar-track {
        background: transparent;
      }

      .warpway-widget-action-pills::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 2px;
      }

      .warpway-widget-action-pill {
        flex-shrink: 0;
        padding: 5px 10px;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        font-size: 12px;
        color: #374151;
        cursor: pointer;
        transition: all 0.2s;
        white-space: nowrap;
      }

      .warpway-widget-action-pill:hover {
        background: #f9fafb;
        border-color: ${this.config.brandColor};
        color: ${this.config.brandColor};
      }

      @media (max-width: 480px) {
        .warpway-widget-chat {
          width: calc(100vw - 32px);
          left: 16px !important;
          right: 16px !important;
        }
      }
    `
    document.head.appendChild(style)
  }

  /**
   * Create the FAB (Floating Action Button)
   */
  private createFAB(): void {
    const fab = document.createElement('button')
    fab.className = 'warpway-widget-fab'
    fab.setAttribute('aria-label', 'Open chat widget')

    const iconType = this.config.iconType || 'chat'

    // If custom icon type and logo URL is provided, use custom logo
    if (iconType === 'custom' && this.config.logoUrl) {
      const fallbackIcon = getIconSVG('chat')
      fab.innerHTML = `
        <img
          src="${this.config.logoUrl}"
          alt="Chat"
          class="warpway-widget-fab-logo"
          onerror="this.style.display='none'; this.nextElementSibling.style.display='block';"
        />
        <div style="display: none;">
          ${fallbackIcon}
        </div>
      `
    } else {
      // Use predefined icon
      const iconSVG = getIconSVG(iconType)
      fab.innerHTML = iconSVG
    }

    fab.addEventListener('click', () => this.toggle())
    this.fab = fab
  }

  /**
   * Create the chat window
   */
  private createChatWindow(): void {
    const chat = document.createElement('div')
    chat.className = 'warpway-widget-chat'

    // Build header with optional logo
    const headerLogo = this.config.logoUrl
      ? `<img src="${this.config.logoUrl}" alt="" class="warpway-widget-header-logo" onerror="this.style.display='none';" />`
      : ''

    // Build footer for free plan users
    const footer = this.config.plan === 'free'
      ? `<div class="warpway-widget-footer">
          Powered by <a href="https://warpway.ai" target="_blank" rel="noopener noreferrer">Warpway</a>
        </div>`
      : ''

    chat.innerHTML = `
      <div class="warpway-widget-header">
        <div class="warpway-widget-header-content">
          ${headerLogo}
          <span>${this.config.title}</span>
        </div>
        <div class="warpway-widget-header-actions">
          <button class="warpway-widget-new-chat" id="warpway-widget-new-chat" aria-label="New chat" title="Start new conversation">
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <button class="warpway-widget-close" aria-label="Close chat">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      <div class="warpway-widget-messages" id="warpway-widget-messages">
        <!-- Welcome message will be loaded dynamically -->
      </div>
      ${footer}
      <div class="warpway-widget-action-pills-container" id="warpway-widget-action-pills-container" style="display: none;">
        <div class="warpway-widget-action-pills-header">Try these actions:</div>
        <div class="warpway-widget-action-pills" id="warpway-widget-action-pills">
          <!-- Action pills will be loaded dynamically -->
        </div>
      </div>
      <div class="warpway-widget-input-container">
        <textarea
          class="warpway-widget-input"
          id="warpway-widget-input"
          placeholder="Type your request..."
          aria-label="Chat input"
          rows="1"
        ></textarea>
        <button class="warpway-widget-send" id="warpway-widget-send">
          Send
        </button>
      </div>
    `

    const closeBtn = chat.querySelector('.warpway-widget-close')
    closeBtn?.addEventListener('click', () => this.close())

    const newChatBtn = chat.querySelector('#warpway-widget-new-chat')
    newChatBtn?.addEventListener('click', () => this.clearConversationHistory())

    const input = chat.querySelector('#warpway-widget-input') as HTMLTextAreaElement
    const sendBtn = chat.querySelector('#warpway-widget-send') as HTMLButtonElement

    // Auto-resize textarea as content changes
    input?.addEventListener('input', () => {
      if (input) {
        input.style.height = 'auto'
        input.style.height = Math.min(input.scrollHeight, 120) + 'px'
      }
    })

    // Handle shift+enter for new line, enter alone to send
    input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey && !this.isExecuting) {
        e.preventDefault()
        this.handleUserMessage(input.value)
        input.value = ''
        input.style.height = 'auto'
      }
    })

    sendBtn?.addEventListener('click', () => {
      if (!this.isExecuting && input.value.trim()) {
        this.handleUserMessage(input.value)
        input.value = ''
        input.style.height = 'auto'
      }
    })

    this.chatWindow = chat
  }

  /**
   * Fetch available actions from the backend
   */
  private async fetchAvailableActions(): Promise<void> {
    try {
      const apiBase = getApiBaseUrl()
      const currentUrl = window.location.href
      const pageTitle = document.title

      const url = new URL(`${apiBase}/v1/conversations/actions`)
      url.searchParams.append('current_url', currentUrl)
      url.searchParams.append('page_title', pageTitle)

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'X-Warpway-Site-Id': this.config.siteId,
        },
      })

      if (!response.ok) {
        console.error('[Warpway Widget] Failed to fetch available actions')
        return
      }

      const data = await response.json()
      if (data.actions && data.actions.length > 0) {
        this.displayActionPills(data.actions)
      }
    } catch (error) {
      console.error('[Warpway Widget] Error fetching available actions:', error)
    }
  }

  /**
   * Display action pills above the input
   */
  private displayActionPills(actions: any[]): void {
    const pillsContainer = this.chatWindow?.querySelector('#warpway-widget-action-pills') as HTMLElement
    const outerContainer = this.chatWindow?.querySelector('#warpway-widget-action-pills-container') as HTMLElement
    if (!pillsContainer || !outerContainer) return

    // Clear existing pills
    pillsContainer.innerHTML = ''

    // Add pills for each action
    actions.forEach((action) => {
      const pill = document.createElement('button')
      pill.className = 'warpway-widget-action-pill'
      pill.textContent = action.name
      pill.title = action.description || action.name
      pill.addEventListener('click', () => {
        this.handleActionPillClick(action.name)
      })
      pillsContainer.appendChild(pill)
    })

    // Show the outer container
    outerContainer.style.display = 'block'

    // Scroll to bottom after pills are shown (they may push content up)
    setTimeout(() => this.scrollToBottom(), 50)
  }

  /**
   * Handle click on an action pill
   */
  private handleActionPillClick(actionName: string): void {
    const input = this.chatWindow?.querySelector('#warpway-widget-input') as HTMLTextAreaElement
    if (input) {
      input.value = actionName
      input.focus()
      // Auto-resize the textarea
      input.style.height = 'auto'
      input.style.height = Math.min(input.scrollHeight, 120) + 'px'
    }
  }

  /**
   * Handle user message with state machine
   */
  private async handleUserMessage(message: string, isRetry: boolean = false): Promise<void> {
    if (!message.trim()) return

    // Add user message to chat (only if not a retry)
    if (!isRetry) {
      this.addMessage(message, 'user')
    }

    // Set loading state
    this.isExecuting = true
    this.updateSendButton()

    // Show typing indicator
    this.showTypingIndicator()

    try {
      const apiBase = getApiBaseUrl()

      // Send message to conversation endpoint
      const response = await fetch(`${apiBase}/v1/conversations/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Warpway-Site-Id': this.config.siteId,
        },
        body: JSON.stringify({
          session_id: this.sessionId,
          message: message,
          bypass_cache: isRetry,  // Bypass cache on retry
          conversation_history: this.conversationHistory,  // Include conversation context
          context: {
            current_url: window.location.href,
            page_title: document.title,
          },
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to send message')
      }

      const data = await response.json()

      // Hide typing indicator before showing response
      this.hideTypingIndicator()

      // Update conversation state
      this.conversationId = data.conversation_id
      this.conversationState = data.state

      // Check if no action was found and this isn't already a retry
      const noActionFound = data.state === 'listening' && !data.action && !isRetry
      if (noActionFound) {
        console.log('[Warpway Widget] No action found in cache, retrying with cache bust...')
        // Remove the "not sure" message before retrying
        // Don't add it to the UI yet

        // Retry with cache bypass
        return this.handleUserMessage(message, true)
      }

      // Display assistant message
      if (data.message) {
        this.addMessage(data.message, 'bot')
      }

      // Handle different states
      switch (data.state) {
        case 'executing':
          // Execute the action
          if (data.action) {
            await this.executeAction(data.action, data.collected_inputs || {})
          }
          break

        case 'collecting_inputs':
        case 'confirming':
        case 'listening':
        case 'help':
          // Just waiting for more user input
          this.isExecuting = false
          this.updateSendButton()
          this.focusInput()
          break

        case 'completed':
        case 'error':
          this.isExecuting = false
          this.updateSendButton()
          this.focusInput()
          break

        default:
          this.isExecuting = false
          this.updateSendButton()
          this.focusInput()
      }

    } catch (error) {
      console.error('[Warpway Widget] Error:', error)
      this.hideTypingIndicator()
      const errorMessage = "Sorry, I encountered an error. Please try again."
      this.addMessage(errorMessage, 'bot')
      this.isExecuting = false
      this.updateSendButton()
      this.focusInput()
    }
  }

  /**
   * Generate human-readable description for a step
   */
  private getStepDescription(step: any, inputs: Record<string, string>): string {
    const stepType = step.type || ''

    if (stepType === 'click') {
      const selectors = step.selectors || []
      if (selectors.length > 0) {
        const selector = selectors[0].selector || ''
        // Extract button text from selector
        const textMatch = selector.match(/text\(["'](.+?)["']\)|has-text\(["'](.+?)["']\)/)
        if (textMatch) {
          const buttonText = textMatch[1] || textMatch[2]
          return `Click '${buttonText}' button`
        }
      }
      return 'Click button'
    }

    if (stepType === 'fill') {
      const selectors = step.selectors || []
      if (selectors.length > 0) {
        const selector = selectors[0].selector || ''
        // Extract field name
        let fieldName = 'field'
        if (selector.includes('#')) {
          fieldName = selector.split('#')[1].split('[')[0].split('.')[0]
        } else if (selector.includes('name=')) {
          const nameMatch = selector.match(/name=["']([^"']+)["']/)
          if (nameMatch) fieldName = nameMatch[1]
        }

        // Show value if available
        const value = step.value || ''
        if (value) {
          return `Enter '${value}' in ${fieldName}`
        }
        return `Fill ${fieldName}`
      }
      return 'Fill field'
    }

    if (stepType === 'navigate') {
      const url = step.url || ''
      return `Navigate to ${url}`
    }

    return stepType
  }

  /**
   * Create or update progress message with step statuses
   */
  private updateProgress(steps: any[], currentStepIndex: number, progressMessageId: string): void {
    const progressLines: string[] = []

    steps.forEach((step, index) => {
      const description = this.getStepDescription(step, {})
      let status = ''

      if (index < currentStepIndex) {
        status = '✓'  // Completed
      } else if (index === currentStepIndex) {
        status = '⏳'  // In progress
      } else {
        status = ' '  // Pending
      }

      progressLines.push(`${status} ${description}`)
    })

    const progressContent = progressLines.join('\n')

    // Find and update the progress message
    const progressElement = document.getElementById(progressMessageId)
    if (progressElement) {
      const contentElement = progressElement.querySelector('.warpway-message-content')
      if (contentElement) {
        contentElement.textContent = progressContent
      }
    }
  }

  /**
   * Execute an action with inputs
   */
  private async executeAction(action: any, inputs: any): Promise<void> {
    try {
      console.log('[Warpway Widget] Executing action:', action)
      const apiBase = getApiBaseUrl()

      // Start run
      const runResponse = await fetch(`${apiBase}/v1/runs/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Warpway-Site-Id': this.config.siteId,
        },
        body: JSON.stringify({
          action_id: action.id,
          inputs: inputs || {},
          session_id: this.sessionId,  // Send session for analytics tracking
        }),
      })

      if (!runResponse.ok) {
        throw new Error('Failed to start run')
      }

      const runData = await runResponse.json()
      const runId = runData.run_id

      // Validate action has manifest and steps
      if (!action.manifest || !action.manifest.steps || !Array.isArray(action.manifest.steps)) {
        throw new Error('Action has no valid manifest or steps')
      }

      if (action.manifest.steps.length === 0) {
        throw new Error('Action has no steps to execute')
      }

      // Inject input values into steps before execution
      const stepsWithInputs = JSON.parse(JSON.stringify(action.manifest.steps))

      // Convert action.inputs to a lookup map (handle both array and object formats)
      const inputConfigMap: Record<string, any> = {}
      if (action.inputs) {
        if (Array.isArray(action.inputs)) {
          // Array format: [{ name: "email", stepIndex: 1, ... }]
          action.inputs.forEach((input: any) => {
            if (input.name) {
              inputConfigMap[input.name] = input
            }
          })
        } else if (typeof action.inputs === 'object') {
          // Object format: { "email": { stepIndex: 1, ... } }
          Object.assign(inputConfigMap, action.inputs)
        }
      }

      // Track which steps should be replaced with user input vs skipped
      const stepsToSkip = new Set<number>()

      // For each input config, either replace value or mark to skip
      for (const [configName, config] of Object.entries(inputConfigMap)) {
        if (typeof config.stepIndex === 'number') {
          const stepIndex = config.stepIndex
          const step = stepsWithInputs[stepIndex]

          if (step && step.type === 'fill') {
            if (configName in inputs) {
              // User provided this input - replace the value
              stepsWithInputs[stepIndex].value = inputs[configName]
            } else {
              // User didn't provide this optional input - skip this step
              stepsToSkip.add(stepIndex)
            }
          }
        }
      }

      // Filter out steps that should be skipped
      const filteredSteps = stepsWithInputs.filter((_: any, idx: number) => !stepsToSkip.has(idx))

      console.log('[Warpway Widget] Steps to execute:', {
        original: stepsWithInputs.length,
        filtered: filteredSteps.length,
        skipped: stepsToSkip.size
      })

      if (filteredSteps.length === 0) {
        throw new Error('No steps to execute after filtering')
      }

      // Build manifest object with starting URL and filtered steps
      const manifestToExecute = {
        startingUrl: action.manifest.startingUrl,
        steps: filteredSteps
      }

      // Create progress message with unique ID
      const progressMessageId = `progress-${Date.now()}`
      const progressMessage = document.createElement('div')
      progressMessage.id = progressMessageId
      progressMessage.className = 'warpway-message warpway-message-bot'
      progressMessage.innerHTML = `
        <div class="warpway-message-content" style="white-space: pre-line; font-family: monospace; font-size: 13px;"></div>
      `
      if (this.container) {
        const messagesContainer = this.container.querySelector('.warpway-messages')
        if (messagesContainer) {
          messagesContainer.appendChild(progressMessage)
          messagesContainer.scrollTop = messagesContainer.scrollHeight
        }
      }

      // Initialize progress with all steps pending
      try {
        this.updateProgress(filteredSteps, -1, progressMessageId)
      } catch (e) {
        console.error('[Warpway Widget] Failed to initialize progress:', e)
      }

      // Execute the action with manifest (including startingUrl) and progress callback
      const result = await this.executor.execute(manifestToExecute, (stepIndex: number) => {
        // Update progress as each step starts
        try {
          this.updateProgress(filteredSteps, stepIndex, progressMessageId)
        } catch (e) {
          console.error('[Warpway Widget] Failed to update progress:', e)
        }
      })

      // Update progress to show all steps complete
      if (result.success) {
        try {
          this.updateProgress(filteredSteps, filteredSteps.length, progressMessageId)
        } catch (e) {
          console.error('[Warpway Widget] Failed to finalize progress:', e)
        }
      }

      // Finish run
      await fetch(`${apiBase}/v1/runs/${runId}/finish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Warpway-Site-Id': this.config.siteId,
        },
        body: JSON.stringify({
          status: result.success ? 'success' : 'failed',
          duration_ms: result.durationMs,
          failure_message: result.failureMessage,
          failure_step: result.failureStep,
          failure_type: result.failureType,
          steps: result.steps,
        }),
      })

      if (result.success) {
        this.addMessage(`✅ Completed successfully!`, 'bot')
        this.conversationState = 'completed'
      } else {
        this.addMessage(`❌ Failed: ${result.failureMessage}`, 'bot')
        this.conversationState = 'error'
      }

    } catch (error) {
      console.error('[Warpway Widget] Execution error:', error)
      this.addMessage(`❌ Execution failed: ${error}`, 'bot')
      this.conversationState = 'error'
    } finally {
      this.isExecuting = false
      this.updateSendButton()
      this.focusInput()
    }
  }

  /**
   * Add a message to the chat
   */
  private addMessage(text: string, type: 'user' | 'bot' | 'system'): void {
    const messagesContainer = this.chatWindow?.querySelector('#warpway-widget-messages')
    if (!messagesContainer) return

    const message = document.createElement('div')
    message.className = `warpway-widget-message warpway-widget-message-${type}`
    // Convert newlines to <br> tags for proper rendering
    message.innerHTML = text.replace(/\n/g, '<br>')
    messagesContainer.appendChild(message)

    // Save to conversation history
    const role = type === 'user' ? 'user' : (type === 'bot' ? 'assistant' : 'system')
    this.conversationHistory.push({ role, content: text })
    this.saveConversationHistory()

    // Scroll to bottom
    this.scrollToBottom()
  }

  /**
   * Scroll messages container to bottom
   * Uses requestAnimationFrame to ensure DOM has been rendered
   */
  private scrollToBottom(): void {
    const messagesContainer = this.chatWindow?.querySelector('#warpway-widget-messages') as HTMLElement
    if (!messagesContainer) return

    // Use requestAnimationFrame to ensure scroll happens after layout
    requestAnimationFrame(() => {
      messagesContainer.scrollTop = messagesContainer.scrollHeight
    })
  }

  /**
   * Restore conversation history from localStorage
   */
  private restoreConversationHistory(): void {
    const savedHistory = this.getConversationHistory()
    if (savedHistory.length === 0) {
      // No saved history, show welcome message
      this.hasShownWelcome = false
      return
    }

    // Restore messages to DOM
    const messagesContainer = this.chatWindow?.querySelector('#warpway-widget-messages')
    if (!messagesContainer) return

    this.conversationHistory = savedHistory
    this.hasShownWelcome = true // Prevent showing welcome again

    for (const msg of savedHistory) {
      const type = msg.role === 'user' ? 'user' : (msg.role === 'assistant' ? 'bot' : 'system')
      const messageEl = document.createElement('div')
      messageEl.className = `warpway-widget-message warpway-widget-message-${type}`
      messageEl.innerHTML = msg.content.replace(/\n/g, '<br>')
      messagesContainer.appendChild(messageEl)
    }

    // Scroll to bottom
    this.scrollToBottom()
  }

  /**
   * Show typing indicator
   */
  private showTypingIndicator(): void {
    const messagesContainer = this.chatWindow?.querySelector('#warpway-widget-messages')
    if (!messagesContainer) return

    // Don't add if already exists
    if (document.getElementById('warpway-typing-indicator')) return

    const indicator = document.createElement('div')
    indicator.id = 'warpway-typing-indicator'
    indicator.className = 'warpway-widget-typing-indicator'
    indicator.innerHTML = `
      <div class="warpway-widget-typing-dot"></div>
      <div class="warpway-widget-typing-dot"></div>
      <div class="warpway-widget-typing-dot"></div>
    `
    messagesContainer.appendChild(indicator)

    // Scroll to bottom
    this.scrollToBottom()
  }

  /**
   * Hide typing indicator
   */
  private hideTypingIndicator(): void {
    const indicator = document.getElementById('warpway-typing-indicator')
    if (indicator) {
      indicator.remove()
    }
  }

  /**
   * Focus the input field
   */
  private focusInput(): void {
    const input = this.chatWindow?.querySelector('#warpway-widget-input') as HTMLInputElement
    if (input && !input.disabled) {
      // Use setTimeout to ensure focus happens after any DOM updates
      setTimeout(() => input.focus(), 10)
    }
  }

  /**
   * Get missing inputs
   */
  private getMissingInputs(required: any, provided: any): string[] {
    if (!required) return []

    const missing: string[] = []
    for (const [key, config] of Object.entries(required)) {
      const inputConfig = config as any
      if (inputConfig.required && !provided[key]) {
        missing.push(inputConfig.label || key)
      }
    }
    return missing
  }

  /**
   * Update send button state
   */
  private updateSendButton(): void {
    const sendBtn = this.chatWindow?.querySelector('#warpway-widget-send') as HTMLButtonElement
    const input = this.chatWindow?.querySelector('#warpway-widget-input') as HTMLInputElement

    if (sendBtn) {
      sendBtn.disabled = this.isExecuting
      sendBtn.textContent = this.isExecuting ? 'Working...' : 'Send'
    }

    if (input) {
      input.disabled = this.isExecuting
    }
  }

  /**
   * Toggle chat window
   */
  toggle(): void {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  /**
   * Open chat window
   */
  open(): void {
    this.isOpen = true
    this.chatWindow?.classList.add('warpway-widget-chat-open')
    this.fab?.classList.add('warpway-widget-fab-open')

    // Save open state to persist across navigation
    this.saveWidgetOpenState(true)

    // Show instant static welcome message on first open
    if (!this.hasShownWelcome) {
      this.hasShownWelcome = true
      this.addMessage("Hi! I can help you take actions on this site. What would you like to do?", 'bot')
    }

    // Fetch and display available actions
    this.fetchAvailableActions()

    // Scroll to bottom when opening
    this.scrollToBottom()

    // Focus input
    const input = this.chatWindow?.querySelector('#warpway-widget-input') as HTMLTextAreaElement
    input?.focus()
  }

  /**
   * Close chat window
   */
  close(): void {
    this.isOpen = false
    this.chatWindow?.classList.remove('warpway-widget-chat-open')
    this.fab?.classList.remove('warpway-widget-fab-open')

    // Save closed state to persist across navigation
    this.saveWidgetOpenState(false)
  }

  /**
   * Show/hide the widget
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled
    if (this.fab) {
      this.fab.style.display = enabled ? 'flex' : 'none'
    }
    if (!enabled) {
      this.close()
    }
  }
}
